import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { useI18n } from "@/context/I18nContext";
import { usePeriod } from "@/context/PeriodContext";
import SmartCaptureDialog from "@/components/dashboard/SmartCaptureDialog";
import {
  Plus, TrendingUp, Receipt, FileText, Landmark, Coins, Boxes,
  Camera, Mic, Type, Sparkles,
} from "lucide-react";

const actions = [
  { key: "scan",    icon: Camera,     tr: "Belge Tara",     de: "Beleg scannen",    aiTab: "photo",   testid: "qa-scan",    ai: true },
  { key: "voice",   icon: Mic,        tr: "Sesli Kayıt",    de: "Sprachnotiz",      aiTab: "voice",   testid: "qa-voice",   ai: true },
  { key: "smart",   icon: Type,       tr: "Akıllı Metin",   de: "Smart-Text",       aiTab: "text",    testid: "qa-smart",   ai: true },
  { key: "revenue",  icon: TrendingUp, tr: "Gelir Ekle",     de: "Umsatz erfassen",   route: "/finance?tab=revenue&new=1",  testid: "qa-revenue" },
  { key: "expense",  icon: Receipt,    tr: "Gider Ekle",     de: "Ausgabe erfassen",  route: "/finance?tab=expenses&new=1", testid: "qa-expense" },
  { key: "invoice",  icon: FileText,   tr: "Fatura Ekle",    de: "Rechnung erfassen", route: "/finance?tab=invoices&new=1", testid: "qa-invoice" },
  { key: "bank",     icon: Landmark,   tr: "Banka Hareketi", de: "Bankbuchung",       route: "/finance?tab=bank",           testid: "qa-bank" },
  { key: "cash",     icon: Coins,      tr: "Kasa Hareketi",  de: "Kassenbuchung",     comingSoon: true,                     testid: "qa-cash" },
  { key: "stock",    icon: Boxes,      tr: "Stok Girişi",    de: "Wareneingang",      route: "/inventory",                  testid: "qa-stock" },
];

export default function QuickActions() {
  const nav = useNavigate();
  const { lang } = useI18n();
  const { isSelectedClosed, refresh } = usePeriod();
  const [captureOpen, setCaptureOpen] = useState(false);
  const [captureTab, setCaptureTab] = useState("text");

  const handleClick = (a) => {
    if (isSelectedClosed) {
      toast.error(lang === "de" ? "Monat geschlossen — Aktion nicht möglich." : "Ay kapalı — işlem yapılamaz.");
      return;
    }
    if (a.ai) {
      setCaptureTab(a.aiTab);
      setCaptureOpen(true);
      return;
    }
    if (a.comingSoon) {
      toast.info(lang === "de" ? "Bald verfügbar." : "Yakında geliyor.");
      return;
    }
    nav(a.route);
  };

  return (
    <div
      className="rounded-xl border bg-muted/20 p-3 md:p-4 space-y-3"
      data-testid="quick-actions"
    >
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground font-semibold">
            {lang === "de" ? "Schnellaktionen" : "Hızlı İşlemler"}
          </div>
          <div className="text-sm text-muted-foreground flex items-center gap-1">
            <Sparkles className="w-3.5 h-3.5 text-primary" />
            {lang === "de" ? "AI-Erfassung + klassische Formulare." : "AI ile hızlıca ekle · form ile detaylı gir."}
          </div>
        </div>
      </div>
      <div className="grid grid-cols-3 md:grid-cols-6 lg:grid-cols-9 gap-2">
        {actions.map((a) => (
          <Button
            key={a.key}
            variant={a.ai ? "default" : "outline"}
            className={`h-auto py-3 rounded-lg flex flex-col items-center gap-1.5 disabled:opacity-50 ${a.ai ? "shadow-elev-2" : "hover:bg-primary/5"}`}
            onClick={() => handleClick(a)}
            data-testid={a.testid}
            disabled={isSelectedClosed}
          >
            <a.icon className="w-4 h-4" />
            <span className="text-[11px] font-medium leading-tight text-center">
              {lang === "de" ? a.de : a.tr}
            </span>
            {a.ai ? <Sparkles className="w-3 h-3 opacity-80 -mt-1" /> : <Plus className="w-3 h-3 opacity-50 -mt-1" />}
          </Button>
        ))}
      </div>

      <SmartCaptureDialog
        open={captureOpen}
        onOpenChange={setCaptureOpen}
        initialTab={captureTab}
        onCommitted={() => { refresh?.(); toast.success(lang === "de" ? "Erfasst." : "Eklendi."); }}
      />
    </div>
  );
}
